import { rect, aRect, px } from '../../../pixel/canvas';
import { SLOT } from '../slots';
import type { Part } from '../../types';

// Anchor = top of head (x=32, varies by body)
// Hair draws upward (a.y-N) and sideways around the head

export const hair: Record<string, Part> = {
  hair_short: {
    id: 'hair_short', category: 'hair', name: 'Short', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 3, 14, 4, p.glass);      // top cap
      rect(ctx, a.x - 8, a.y + 1, 2, 7, p.glass);    // left side
      rect(ctx, a.x + 6, a.y + 1, 2, 7, p.glass);    // right side
      aRect(ctx, a.x, a.y - 3, 12, 1, p.glassLight);  // shine
    },
  },
  hair_long: {
    id: 'hair_long', category: 'hair', name: 'Long', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 3, 14, 4, p.glass);       // top cap
      rect(ctx, a.x - 8, a.y + 1, 3, 24, p.glass);    // left curtain
      rect(ctx, a.x + 5, a.y + 1, 3, 24, p.glass);    // right curtain
      rect(ctx, a.x - 7, a.y + 2, 1, 22, p.glassLight); // left highlight
      aRect(ctx, a.x, a.y - 3, 10, 1, p.glassLight);   // top shine
    },
  },
  hair_spiky: {
    id: 'hair_spiky', category: 'hair', name: 'Spiky', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      // Spikes going upward
      rect(ctx, a.x - 2, a.y - 8, 4, 9, p.glass);    // center spike
      rect(ctx, a.x - 6, a.y - 6, 3, 7, p.glass);    // left spike
      rect(ctx, a.x + 3, a.y - 6, 3, 7, p.glass);    // right spike
      rect(ctx, a.x - 9, a.y - 3, 2, 4, p.glassDark); // outer left
      rect(ctx, a.x + 7, a.y - 3, 2, 4, p.glassDark); // outer right
      rect(ctx, a.x - 7, a.y + 1, 2, 8, p.glass);    // left side body
      rect(ctx, a.x + 5, a.y + 1, 2, 8, p.glass);    // right side body
    },
  },
  hair_bun: {
    id: 'hair_bun', category: 'hair', name: 'Bun', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 3, 12, 4, p.glass);       // base cap
      // Bun on top (circle-ish)
      aRect(ctx, a.x, a.y - 9, 8, 6, p.glass);        // bun body
      aRect(ctx, a.x, a.y - 10, 6, 2, p.glass);       // bun top
      aRect(ctx, a.x, a.y - 9, 6, 1, p.glassLight);   // bun shine
      rect(ctx, a.x - 7, a.y + 1, 2, 6, p.glass);     // left side
      rect(ctx, a.x + 5, a.y + 1, 2, 6, p.glass);     // right side
    },
  },
  hair_mohawk: {
    id: 'hair_mohawk', category: 'hair', name: 'Mohawk', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      // Central strip going up
      aRect(ctx, a.x, a.y - 10, 4, 11, p.glass);
      aRect(ctx, a.x, a.y - 12, 2, 3, p.glass);        // tip
      aRect(ctx, a.x, a.y - 10, 2, 1, p.glassLight);   // shine
      // Shaved sides — very short
      rect(ctx, a.x - 7, a.y, 1, 5, p.glassDark);
      rect(ctx, a.x + 6, a.y, 1, 5, p.glassDark);
    },
  },
  hair_bob: {
    id: 'hair_bob', category: 'hair', name: 'Bob', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 3, 14, 4, p.glass);        // top cap
      rect(ctx, a.x - 8, a.y + 1, 3, 10, p.glass);     // left curtain (to jaw)
      rect(ctx, a.x + 5, a.y + 1, 3, 10, p.glass);     // right curtain
      // Blunt bottom edge
      rect(ctx, a.x - 8, a.y + 10, 3, 1, p.glassDark); // left bottom
      rect(ctx, a.x + 5, a.y + 10, 3, 1, p.glassDark); // right bottom
      aRect(ctx, a.x, a.y - 3, 10, 1, p.glassLight);   // top shine
    },
  },
  hair_afro: {
    id: 'hair_afro', category: 'hair', name: 'Afro', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      // Large rounded puff
      aRect(ctx, a.x, a.y - 8, 20, 14, p.glass);       // main mass
      aRect(ctx, a.x, a.y - 10, 16, 4, p.glass);       // top dome
      aRect(ctx, a.x, a.y - 11, 10, 2, p.glass);       // apex
      // Trim the corners to round it
      px(ctx, a.x - 10, a.y - 8, '#00000000');
      aRect(ctx, a.x, a.y - 7, 2, 1, p.glassLight);    // shine
    },
  },
  hair_ponytail: {
    id: 'hair_ponytail', category: 'hair', name: 'Ponytail', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 3, 12, 4, p.glass);        // top cap
      rect(ctx, a.x - 7, a.y + 1, 2, 8, p.glass);      // left side
      // Ponytail (right side, hanging)
      rect(ctx, a.x + 5, a.y + 1, 3, 4, p.glass);      // gather
      rect(ctx, a.x + 6, a.y + 4, 2, 18, p.glass);     // tail hanging down
      rect(ctx, a.x + 6, a.y + 5, 1, 16, p.glassLight);// tail highlight
      // Tie/band
      aRect(ctx, a.x + 7, a.y + 4, 4, 2, p.accent);
    },
  },
  hair_bald: {
    id: 'hair_bald', category: 'hair', name: 'Bald', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      // Just a shine/highlight on the head
      aRect(ctx, a.x - 2, a.y + 2, 4, 2, p.glassLight);
      aRect(ctx, a.x - 1, a.y + 1, 2, 1, p.glassLight);
    },
  },
  hair_bangs: {
    id: 'hair_bangs', category: 'hair', name: 'Bangs', requires: SLOT.HEAD,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 2, 14, 3, p.glass);        // top cap
      // Fringe across forehead (hangs down from top)
      aRect(ctx, a.x, a.y, 12, 4, p.glass);            // bangs covering forehead
      aRect(ctx, a.x, a.y + 3, 8, 1, p.glassDark);     // bang tips (uneven)
      px(ctx, a.x - 4, a.y + 4, p.glass);
      px(ctx, a.x + 3, a.y + 4, p.glass);
      rect(ctx, a.x - 7, a.y + 1, 2, 8, p.glass);      // left side
      rect(ctx, a.x + 5, a.y + 1, 2, 8, p.glass);      // right side
      aRect(ctx, a.x, a.y - 2, 10, 1, p.glassLight);
    },
  },
};
